import React, { useState, useEffect } from "react";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import QueueItem from "../AudioPlayer/QueueItem";
import { useAuth } from "@/contexts/AuthContext";
import LoadMoreItem from "../AudioPlayer/LoadMoreItem";
import { api } from "@/lib/api";
import { LoadingState } from "../ui/loading-state";
import type { Episode } from "@/types/episode";

const PAGE_SIZE = 20;

const ListenHistory = () => {
  const { 
    currentEpisode, 
    playEpisode,
    setQueueEpisodes,
    setDisplayedEpisodes,
    setAllEpisodes,
    setOnEndedCallback
  } = useAudioPlayer();
  const { user } = useAuth();
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const loadHistory = async (pageNum: number, append: boolean = false) => {
    if (!user || loading) return;

    try {
      setLoading(true);
      const { episodes: newEpisodes, hasMore: more } =
        await api.episodes.getListenHistory(user.id, pageNum, PAGE_SIZE);

      setEpisodes((prev) => {
        const allEpisodes = append ? [...prev, ...newEpisodes] : newEpisodes;
        setAllEpisodes(allEpisodes);
        return allEpisodes;
      });
      setHasMore(more);
    } catch (error) {
      console.error("Error loading listen history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayEpisode = (episode: Episode) => {
    // Find the index of the clicked episode
    const episodeIndex = episodes.findIndex(ep => ep.id === episode.id);
    if (episodeIndex === -1) return;

    // Get the remaining episodes starting from the clicked one
    const remainingEpisodes = episodes.slice(episodeIndex + 1);
    console.log('Setting up listen history queue with remaining episodes:', remainingEpisodes.length);

    // Set up the queue with remaining episodes
    setQueueEpisodes(remainingEpisodes);
    setDisplayedEpisodes(remainingEpisodes);
    
    // Play the selected episode with history reset
    playEpisode(episode, false, { resetHistory: true });
  };

  useEffect(() => {
    const handleNextTrack = () => {
      if (!episodes.length) return;

      // Find the current episode index
      const currentIndex = episodes.findIndex(ep => ep.id === currentEpisode?.id);
      if (currentIndex === -1 || currentIndex === episodes.length - 1) return;

      // Get the next episode
      const nextEpisode = episodes[currentIndex + 1];
      if (nextEpisode) {
        // Update queue with remaining episodes
        const remainingEpisodes = episodes.slice(currentIndex + 2);
        setQueueEpisodes(remainingEpisodes);
        setDisplayedEpisodes(remainingEpisodes);
        playEpisode(nextEpisode, true);
      }
    };

    setOnEndedCallback(handleNextTrack);
  }, [episodes, currentEpisode, playEpisode, setOnEndedCallback]);

  // Load initial history
  useEffect(() => {
    if (user) {
      loadHistory(1);
    }
  }, [user]);

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadHistory(nextPage, true);
    }
  };

  if (episodes.length === 0 && !loading) {
    return (
      <div className="w-full bg-white/20 backdrop-blur-md rounded-3xl border border-white/20 shadow-xl overflow-hidden">
        <div className="p-6 flex flex-col items-center justify-center">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            Listen History
          </h2>
          <p className="text-gray-600">No listening history yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white/20 backdrop-blur-md rounded-3xl border border-white/20 shadow-xl overflow-hidden flex flex-col h-full">
      <div className="flex-none px-4 py-3 border-b border-white/5">
        <h2 className="text-sm font-medium text-gray-800">Listen History</h2>
      </div>
      <ScrollArea className="flex-1">
        <div className="divide-y divide-white/5">
          {loading && episodes.length === 0 ? (
            <LoadingState />
          ) : (
            <>
              {episodes.map((episode) => (
                <QueueItem
                  key={episode.id}
                  title={episode.title}
                  duration={episode.duration}
                  artist={episode.podcastName}
                  imageUrl={episode.imageUrl}
                  listens={episode.listens}
                  likes={episode.likes}
                  isActive={episode.id === currentEpisode?.id}
                  onClick={() => handlePlayEpisode(episode)}
                />
              ))}
              {hasMore && (
                <LoadMoreItem
                  onClick={loadMore}
                  loading={loading}
                />
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ListenHistory;
